import { cva, type VariantProps } from 'class-variance-authority';

import { Badge } from '@app/shared/ui/atoms/Common';
import { PriceType } from '@app/shared/types/common';
import { cookieImage, diamondIcon } from '@app/assets';
import { cn } from '@app/shared/utils/common';

const priceBadgeVariants = cva(
  'absolute right-1.5 top-1.5 z-20 flex items-center gap-0.5 rounded-full text-orange-500',
  {
    variants: {
      type: {
        [PriceType.COOKIE]: 'bg-yellow',
        [PriceType.TON]: 'bg-white',
      },
      size: {
        small: 'px-1 py-[2px] text-2xs',
        large: 'text-sm leading-none',
      },
    },
    defaultVariants: {
      size: 'small',
    },
  }
);

type PriceBadgeSize = 'small' | 'large';

const ICON_SIZE_MAP: Record<PriceBadgeSize, string> = {
  small: 'h-2',
  large: 'h-[14px]',
};

const BADGE_TYPE_MAP: Record<PriceType, { icon: string; alt: string }> = {
  [PriceType.COOKIE]: { icon: cookieImage, alt: 'cookie icon' },
  [PriceType.TON]: { icon: diamondIcon, alt: 'diamond icon' },
};

interface PriceBadgeProps extends VariantProps<typeof priceBadgeVariants> {
  type: PriceType;
  price: number;
  size?: PriceBadgeSize;
  className?: string;
}

export function PriceBadge({ type, price, size = 'large', className }: PriceBadgeProps) {
  return (
    <Badge className={cn(priceBadgeVariants({ type, size }), className)}>
      <span>{price}</span>
      <img
        src={BADGE_TYPE_MAP[type].icon}
        alt={BADGE_TYPE_MAP[type].alt}
        className={ICON_SIZE_MAP[size]}
      />
    </Badge>
  );
}
